import { STORAGE_KEYS } from "@/constants/storageKeys";
import type { ApiResponse, PaginatedResponse } from "@/types";
import { getEnvConfig } from "./runtime-env";

function isAuthFailure(
  status: number,
  data: Pick<ApiResponse, "code" | "message">,
) {
  if (
    status === 401 ||
    status === 403 ||
    data.code === 401 ||
    data.code === 403
  ) {
    return true;
  }
  return /invalid authorization token/i.test(data.message || "");
}

export function isAuthFailureError(error: unknown) {
  return (
    error instanceof Error &&
    /invalid authorization token|missing authorization token|invalid refresh token|session expired/i.test(
      error.message,
    )
  );
}

class ApiClient {
  private token: string | null = null;
  private authFailureHandler: (() => void) | null = null;
  private authRefreshHandler: (() => Promise<string | null>) | null = null;

  constructor() {
    if (typeof window !== "undefined") {
      this.token = localStorage.getItem(STORAGE_KEYS.accessToken);
    }
  }

  private getCurrentToken(): string | null {
    return typeof window !== "undefined"
      ? localStorage.getItem(STORAGE_KEYS.accessToken)
      : this.token;
  }

  private getBaseURL(): string {
    return getEnvConfig().API_BASE_URL;
  }

  private getUploadCandidates(endpoint: string): string[] {
    if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
      return [endpoint];
    }

    const config = getEnvConfig();
    const candidates: string[] = [];
    const uploadBase = config.UPLOAD_API_BASE_URL.replace(/\/+$/, "");

    if (uploadBase && endpoint === "/files/upload") {
      candidates.push(`${uploadBase}/api/v1/files/upload`);
    }

    candidates.push(`${this.getBaseURL()}${endpoint}`);

    return [...new Set(candidates)];
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    authRetried = false,
    silentAuthFailure = false,
  ): Promise<ApiResponse<T>> {
    const url =
      endpoint.startsWith("http://") || endpoint.startsWith("https://")
        ? endpoint
        : `${this.getBaseURL()}${endpoint}`;
    const isFormData =
      typeof FormData !== "undefined" && options.body instanceof FormData;
    const headers: Record<string, string> = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(options.headers as Record<string, string>),
    };

    const currentToken = this.getCurrentToken();

    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const rawText = await response.text();
    let data: ApiResponse<T>;
    try {
      data = rawText
        ? (JSON.parse(rawText) as ApiResponse<T>)
        : {
            code: response.ok ? 0 : response.status,
            message: "",
            data: undefined as T,
          };
    } catch {
      data = { code: response.status, message: rawText, data: undefined as T };
    }

    if (isAuthFailure(response.status, data)) {
      if (
        !silentAuthFailure &&
        !authRetried &&
        endpoint !== "/auth/refresh" &&
        this.authRefreshHandler
      ) {
        const refreshedToken = await this.authRefreshHandler();
        if (refreshedToken) {
          return this.request<T>(endpoint, options, true, silentAuthFailure);
        }
      }
      if (!silentAuthFailure) {
        this.authFailureHandler?.();
      }
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  }

  get<T>(
    endpoint: string,
    options?: { params?: Record<string, unknown> },
  ): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (options?.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url = `${endpoint}?${queryString}`;
      }
    }
    return this.request<T>(url, { method: "GET" });
  }

  post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  postSilently<T>(endpoint: string, data?: unknown, options: RequestInit = {}) {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      },
      false,
      true,
    );
  }

  put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async upload<T>(
    endpoint: string,
    file: File,
    fields?: Record<string, string>,
    options: RequestInit = {},
    silentAuthFailure = false,
    useUploadApiCandidates = true,
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);
    Object.entries(fields || {}).forEach(([key, value]) => {
      formData.append(key, value);
    });
    const candidates = useUploadApiCandidates
      ? this.getUploadCandidates(endpoint)
      : [`${this.getBaseURL()}${endpoint}`];
    let lastError: unknown;

    const isRetryableUploadFailure = (
      response: Pick<ApiResponse<unknown>, "code" | "message">,
    ) => {
      const message = response.message || "";
      return (
        response.code >= 500 ||
        /failed to upload file/i.test(message) ||
        /storage manager is not initialized/i.test(message)
      );
    };

    for (const [index, url] of candidates.entries()) {
      try {
        const result = await this.request<T>(
          url,
          {
            ...options,
            method: "POST",
            body: formData,
            headers: {},
          },
          false,
          silentAuthFailure,
        );
        if (
          index < candidates.length - 1 &&
          isRetryableUploadFailure(result)
        ) {
          lastError = new Error(result.message || "Upload failed");
          continue;
        }
        return result;
      } catch (error) {
        lastError = error;
        const message = error instanceof Error ? error.message : String(error);
        const shouldTryNext =
          candidates.length > 1 &&
          (message.includes("status: 404") ||
            message.includes("status: 405") ||
            /status: 5\d\d/.test(message) ||
            /not found/i.test(message) ||
            /failed to upload file/i.test(message) ||
            /storage manager is not initialized/i.test(message) ||
            message.includes("<!DOCTYPE") ||
            message.includes("<html"));

        if (!shouldTryNext) {
          throw error;
        }
      }
    }

    throw lastError instanceof Error ? lastError : new Error("Upload failed");
  }

  uploadSilently<T>(
    endpoint: string,
    file: File,
    fields?: Record<string, string>,
    options: RequestInit = {},
  ) {
    return this.upload<T>(
      endpoint,
      file,
      fields,
      { ...options, headers: {} },
      true,
    );
  }

  uploadToBackend<T>(
    endpoint: string,
    file: File,
    fields?: Record<string, string>,
  ) {
    return this.upload<T>(endpoint, file, fields, {}, false, false);
  }

  delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  getPaginated<T>(
    endpoint: string,
    params?: { page?: number; page_size?: number; [key: string]: unknown },
  ): Promise<ApiResponse<PaginatedResponse<T>>> {
    return this.get<PaginatedResponse<T>>(endpoint, { params });
  }

  setToken(token: string) {
    this.token = token;
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEYS.accessToken, token);
    }
  }

  clearToken() {
    this.token = null;
    if (typeof window !== "undefined") {
      localStorage.removeItem(STORAGE_KEYS.accessToken);
    }
  }

  getToken(): string | null {
    return this.token;
  }

  setAuthFailureHandler(handler: (() => void) | null) {
    this.authFailureHandler = handler;
  }

  setAuthRefreshHandler(handler: (() => Promise<string | null>) | null) {
    this.authRefreshHandler = handler;
  }
}

export const apiClient = new ApiClient();
